import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { Setting } from '../../types';
import RichTextEditor from './RichTextEditor';
import './SettingsList.css';

const AdminSettingsList: React.FC = () => {
  const { isAdmin } = useAuth();
  const [settings, setSettings] = useState<Setting[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<{ [key: number]: boolean }>({});
  const [errors, setErrors] = useState<{ [key: number | string]: string }>({});

  /**
   * Format setting key for display
   * Replaces underscores with spaces and capitalizes each word
   * Example: "email_content" -> "Email Content"
   */
  const formatSettingKey = (key: string): string => {
    return key
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  useEffect(() => {
    if (isAdmin()) {
      fetchSettings();
    }
  }, [isAdmin]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      // Fetch admin settings (client_id = 0)
      const response = await api.get<Setting[]>(`/settings?client_id=0`);
      setSettings(response.data);
      setErrors({});
    } catch (error: any) {
      console.error('Failed to fetch admin settings:', error);
      if (error.response?.status === 403) {
        setErrors({ general: 'You do not have access to these settings.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (setting: Setting, newValue: string) => {
    try {
      setSaving({ ...saving, [setting.id]: true });
      setErrors({ ...errors, [setting.id]: '' });

      const response = await api.put<Setting>(`/settings/${setting.id}`, {
        value: newValue,
      });

      setSettings(settings.map(s => s.id === setting.id ? response.data : s));
    } catch (error: any) {
      console.error('Failed to update setting:', error);
      setErrors({
        ...errors,
        [setting.id]: error.response?.data?.message || 'Failed to update setting',
      });
    } finally {
      setSaving({ ...saving, [setting.id]: false });
    }
  };

  const handleInputChange = (settingId: number, value: string) => {
    setSettings(settings.map(s => 
      s.id === settingId ? { ...s, value } : s
    ));
  };

  // Determine input type based on key
  const getInputType = (key: string): 'password' | 'text' | 'number' | 'select' | 'content' => {
    if (key.includes('password')) {
      return 'password';
    }
    if (key === 'client_user_count' || key.includes('port')) {
      return 'number';
    }
    if (key.includes('encryption')) {
      return 'select';
    }
    if (key.includes('content')) {
      return 'content';
    }
    return 'text';
  };

  if (loading) {
    return <div className="loading">Loading admin settings...</div>;
  }

  if (!isAdmin()) {
    return <div className="no-data">You do not have permission to view admin settings.</div>;
  }

  return (
    <div className="settings-list">
      <div className="admin-settings-info">
        <p>Configure SMTP email settings for the application. These settings will be used for all outgoing emails.</p>
      </div>

      {errors.general && (
        <div className="alert alert-error">{errors.general}</div>
      )}

      {settings.length === 0 ? (
        <div className="no-data">No admin settings found. Please run the seeder to create default settings.</div>
      ) : (
        <div className="settings-table-container">
          <table className="settings-table">
            <thead>
              <tr>
                <th>Setting</th>
                <th>Value</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {settings.map((setting) => {
                const inputType = getInputType(setting.key);
                
                return (
                  <tr key={setting.id}>
                    <td className="setting-key">
                      <span className="key-label">{formatSettingKey(setting.key)}</span>
                    </td>
                    <td className="setting-value">
                      {inputType === 'password' ? (
                        <input
                          type="password"
                          value={setting.value || ''}
                          onChange={(e) => handleInputChange(setting.id, e.target.value)}
                          className="setting-input"
                          placeholder="Enter password"
                        />
                      ) : inputType === 'number' ? (
                        <input
                          type="number"
                          value={setting.value || ''}
                          onChange={(e) => handleInputChange(setting.id, e.target.value)}
                          className="setting-input"
                          placeholder="Enter port number"
                        />
                      ) : inputType === 'select' ? (
                        <select
                          value={setting.value || 'tls'}
                          onChange={(e) => handleInputChange(setting.id, e.target.value)}
                          className="setting-input"
                        >
                          <option value="tls">TLS</option>
                          <option value="ssl">SSL</option>
                          <option value="">None</option>
                        </select>
                      ) : inputType === 'content' ? (
                        <RichTextEditor
                          value={setting.value || ''}
                          onChange={(value) => handleInputChange(setting.id, value)}
                          placeholder="Enter content"
                        />
                      ) : (
                        <input
                          type="text"
                          value={setting.value || ''}
                          onChange={(e) => handleInputChange(setting.id, e.target.value)}
                          className="setting-input"
                          placeholder={`Enter ${formatSettingKey(setting.key)}`}
                        />
                      )}
                    </td>
                    <td className="setting-actions">
                      <button
                        onClick={() => handleUpdate(setting, setting.value || '')}
                        disabled={saving[setting.id]}
                        className="btn btn-primary btn-sm"
                      >
                        {saving[setting.id] ? 'Saving...' : 'Save'}
                      </button>
                      {errors[setting.id] && (
                        <div className="error-message">{errors[setting.id]}</div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminSettingsList;
